import { useEffect, useMemo, useRef, useState } from "react";
import {
  Search, Camera, X, Sparkles,
  User, RotateCcw, ArrowLeft, ArrowRight, Users,
  ArrowUpFromLine, ArrowDownFromLine, ChevronsUp, ChevronsDown,
  Spline, MoveHorizontal, ZoomIn, Focus, Expand, Frame,
} from "lucide-react";

/**
 * 16 filmmaker/portrait camera angles for AI Image generation. Selecting
 * one appends a concrete angle instruction to the current prompt so the
 * model gets an exact framing cue.
 */
export const CAMERA_ANGLES = [
  { id: "front-view",           name: "Front View",            icon: User,             instruction: "front-facing view of the subject, direct eye contact with camera" },
  { id: "back-view",            name: "Back View",             icon: RotateCcw,        instruction: "back view of the subject, facing away from camera" },
  { id: "side-left",            name: "Side View · Left",      icon: ArrowLeft,        instruction: "profile side view of the subject facing left" },
  { id: "side-right",           name: "Side View · Right",     icon: ArrowRight,       instruction: "profile side view of the subject facing right" },
  { id: "three-quarter-front",  name: "3/4 Front View",        icon: Users,            instruction: "three-quarter front view — subject angled 45° toward camera" },
  { id: "three-quarter-back",   name: "3/4 Back View",         icon: Users,            instruction: "three-quarter back view — subject angled 45° away from camera" },
  { id: "birds-eye",            name: "Bird's Eye View",       icon: ChevronsDown,     instruction: "top-down bird's-eye view looking straight down at the subject" },
  { id: "worms-eye",            name: "Worm's Eye View",       icon: ChevronsUp,       instruction: "ground-level worm's-eye view looking straight up at the subject" },
  { id: "low-angle",            name: "Low Angle",             icon: ArrowUpFromLine,  instruction: "low-angle hero shot looking up at the subject from below" },
  { id: "high-angle",           name: "High Angle",            icon: ArrowDownFromLine,instruction: "high-angle shot looking down at the subject from above" },
  { id: "dutch-angle",          name: "Dutch Angle",           icon: Spline,           instruction: "tilted Dutch angle with a canted horizon for uneasy tension" },
  { id: "over-the-shoulder",    name: "Over the Shoulder",     icon: MoveHorizontal,   instruction: "over-the-shoulder framing with the shoulder in foreground and subject beyond" },
  { id: "close-up",             name: "Close Up",              icon: ZoomIn,           instruction: "close-up framing of the subject's face and shoulders" },
  { id: "extreme-close-up",     name: "Extreme Close Up",      icon: Focus,            instruction: "extreme close-up isolating the eyes and expression" },
  { id: "wide-shot",            name: "Wide Shot",             icon: Expand,           instruction: "wide establishing shot showing the subject small within the full environment" },
  { id: "full-body",            name: "Full Body Shot",        icon: Frame,            instruction: "full-body shot framing the subject from head to toe" },
];

/**
 * CameraAnglePresetsMenu — glassmorphic dark popover with a search bar and
 * a scrollable 2-column grid of 16 camera angles. Selecting one calls
 * onSelect(preset); the parent appends its `instruction` to the prompt.
 */
export function CameraAnglePresetsMenu({ open, onClose, onSelect, activeId = null }) {
  const [q, setQ] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setQ("");
      setTimeout(() => inputRef.current?.focus(), 60);
    }
  }, [open]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return CAMERA_ANGLES;
    return CAMERA_ANGLES.filter(
      (p) => p.name.toLowerCase().includes(needle) || p.instruction.toLowerCase().includes(needle)
    );
  }, [q]);

  if (!open) return null;

  return (
    <div className="mb-2 relative" data-testid="camera-angle-menu-wrap">
      <div
        className="absolute left-2 bottom-0 w-[400px] rounded-2xl border border-[#00E5FF]/25 bg-gradient-to-br from-[#0e0b13]/97 via-[#0a0812]/97 to-[#080810]/97 backdrop-blur-xl shadow-[0_0_48px_-12px_rgba(0,229,255,0.45)] overflow-hidden"
        data-testid="camera-angle-menu"
      >
        {/* Aurora rim — cyan-lean since angles are the "image" counterpart */}
        <span
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-60"
          style={{
            background:
              "radial-gradient(120% 60% at 0% 0%, rgba(0,229,255,0.12), transparent 55%), radial-gradient(120% 60% at 100% 100%, rgba(242,201,76,0.08), transparent 55%)",
          }}
        />

        {/* Header */}
        <div className="relative px-4 py-2.5 border-b border-[#00E5FF]/15 flex items-center justify-between">
          <div className="flex items-center gap-1.5 font-mono-hy text-[9px] text-[#00E5FF] tracking-[0.24em]">
            <Camera className="w-3 h-3" /> CAMERA ANGLE · {CAMERA_ANGLES.length}
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="camera-angle-close"
            className="w-6 h-6 rounded-full border border-white/[0.06] hover:border-[#EB5757]/60 hover:text-[#EB5757] text-white flex items-center justify-center transition-all"
            aria-label="Close camera angle menu"
          >
            <X className="w-3 h-3" />
          </button>
        </div>

        {/* Search */}
        <div className="relative px-3 pt-3 pb-2">
          <div className="relative">
            <Search className="w-3.5 h-3.5 text-white absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              ref={inputRef}
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={`Search ${CAMERA_ANGLES.length} angles…`}
              data-testid="camera-angle-search"
              className="w-full pl-8 pr-3 py-2 rounded-full bg-[#0A0A0A]/70 border border-white/[0.06] focus:border-[#00E5FF]/50 focus:outline-none font-body text-[12px] text-white placeholder:text-white transition-colors"
            />
          </div>
        </div>

        {/* Grid */}
        <div
          className="relative px-3 pb-3 max-h-[46vh] overflow-y-auto"
          data-testid="camera-angle-grid"
        >
          {filtered.length === 0 ? (
            <div className="py-8 flex flex-col items-center gap-2 text-center">
              <Sparkles className="w-4 h-4 text-[#00E5FF]/50" />
              <div className="font-mono-hy text-[10px] text-white tracking-widest">
                NO ANGLES MATCH "{q}"
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              {filtered.map((p) => {
                const Icon = p.icon;
                const active = activeId === p.id;
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => { onSelect?.(p); onClose?.(); }}
                    data-testid={`camera-angle-${p.id}`}
                    title={p.instruction}
                    className={`group flex items-center gap-2 px-2.5 py-2 rounded-xl border transition-all text-left ${
                      active
                        ? "border-[#00E5FF] bg-[#00E5FF]/10 shadow-[0_0_16px_-6px_rgba(0,229,255,0.6)]"
                        : "border-white/[0.06] bg-white/[0.02] hover:border-[#00E5FF]/50 hover:bg-[#00E5FF]/[0.05] hover:shadow-[0_0_16px_-8px_rgba(0,229,255,0.4)]"
                    }`}
                  >
                    <div
                      className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 border ${
                        active
                          ? "border-[#00E5FF] bg-[#00E5FF]/10"
                          : "border-white/[0.06] bg-[#0A0A0A]/60 group-hover:border-[#00E5FF]/40"
                      }`}
                    >
                      <Icon className={`w-3.5 h-3.5 ${active ? "text-[#00E5FF]" : "text-[#00E5FF]/70 group-hover:text-[#00E5FF]"}`} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className={`font-mono-hy text-[10px] tracking-[0.12em] ${active ? "text-[#00E5FF]" : "text-white"} truncate`}>
                        {p.name}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer hint */}
        <div className="relative px-4 py-2 border-t border-[#00E5FF]/10 bg-[#0A0A0A]/40">
          <div className="font-mono-hy text-[8px] text-white tracking-[0.22em] leading-relaxed">
            TAP AN ANGLE → APPENDED TO YOUR PROMPT
          </div>
        </div>
      </div>
    </div>
  );
}

export default CameraAnglePresetsMenu;
